import React, { useEffect, useState } from 'react';
import { Crown, Users, Star, Phone, Mail, MapPin, Loader2 } from 'lucide-react';

interface CommitteeMember {
  id:string; name:string; role:string; village?:string; title?:string;
  bio?:string; phone?:string; email?:string; photoUrl?:string; sortOrder?:number;
}

export default function ManagementCommitteePanel() {
  const [members, setMembers] = useState<CommitteeMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/public/management-committee')
      .then(r=>r.json()).then(d=>{ setMembers(d.members||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 text-[#C9A84C] animate-spin" />
    </div>
  );

  const chairman  = members.find(m=>m.role==='Chairman');
  const viceChair = members.find(m=>m.role==='Vice-Chairman');
  const leadership = [chairman,viceChair].filter(Boolean) as CommitteeMember[];
  const rest       = members.filter(m=>m.role!=='Chairman'&&m.role!=='Vice-Chairman');

  return (
    <section className="py-16 px-4 bg-[#070D0A]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-[#C9A84C]/10
                          border border-[#C9A84C]/30 rounded-full px-5 py-2 mb-4">
            <Crown className="w-4 h-4 text-[#C9A84C]" />
            <span className="text-[#C9A84C] text-sm font-semibold tracking-widest uppercase">
              Festival Leadership
            </span>
          </div>
          <h2 className="font-cinzel text-3xl md:text-4xl text-[#EDE8DC] mb-3">
            Management Committee
          </h2>
          <p className="text-[#EDE8DC]/60 max-w-xl mx-auto text-sm">
            The organising body responsible for delivering the ERUE Festival
            of Uzairue Kingdom — December 24–30, 2026.
          </p>
          <div className="mt-4 h-px w-24 mx-auto bg-gradient-to-r
                          from-transparent via-[#C9A84C] to-transparent" />
        </div>

        {members.length === 0 ? (
          <div className="text-center py-16 text-[#EDE8DC]/40">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Committee members will be announced soon.</p>
          </div>
        ) : (
          <>
            {leadership.length > 0 && (
              <div className="flex flex-wrap justify-center gap-6 mb-10">
                {leadership.map(m=><MemberCard key={m.id} member={m} featured />)}
              </div>
            )}
            {rest.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {rest.map(m=><MemberCard key={m.id} member={m} />)}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function MemberCard({ member:m, featured=false }:
  { key?:React.Key; member:CommitteeMember; featured?:boolean }) {
  return (
    <div className={`bg-[#0E1812] border border-[#1C2E22] rounded-2xl overflow-hidden
                     hover:border-[#C9A84C]/40 transition-all duration-300 group
                     ${featured?'w-64 flex-shrink-0':''}`}>
      <div className={`relative overflow-hidden bg-[#121E16] ${featured?'h-56':'h-36'}`}>
        {m.photoUrl ? (
          <img src={m.photoUrl} alt={m.name}
               className="w-full h-full object-cover object-top
                          group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#0F5132]/40 border-2
                            border-[#C9A84C]/30 flex items-center justify-center">
              <span className="text-[#C9A84C] font-cinzel text-xl font-bold">
                {m.name.charAt(0)}
              </span>
            </div>
          </div>
        )}
        {featured && (
          <div className="absolute top-3 right-3">
            <Star className="w-4 h-4 text-[#C9A84C] fill-[#C9A84C]"/>
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-[#C9A84C] text-xs font-semibold tracking-wider uppercase mb-1 truncate">
          {m.role}
        </p>
        <h3 className={`text-[#EDE8DC] font-semibold truncate ${featured?'text-base':'text-sm'}`}>
          {m.title?`${m.title} `:''}{m.name}
        </h3>
        {m.village && (
          <div className="flex items-center gap-1 mt-1.5">
            <MapPin className="w-3 h-3 text-[#EDE8DC]/40 flex-shrink-0"/>
            <span className="text-[#EDE8DC]/50 text-xs truncate">{m.village}</span>
          </div>
        )}
        {featured && m.bio && (
          <p className="text-[#EDE8DC]/60 text-xs mt-2 line-clamp-3">{m.bio}</p>
        )}
        {featured && (m.phone||m.email) && (
          <div className="mt-3 space-y-1 border-t border-[#1C2E22] pt-3">
            {m.phone && (
              <div className="flex items-center gap-1.5">
                <Phone className="w-3 h-3 text-[#C9A84C]"/>
                <span className="text-[#EDE8DC]/60 text-xs">{m.phone}</span>
              </div>
            )}
            {m.email && (
              <div className="flex items-center gap-1.5">
                <Mail className="w-3 h-3 text-[#C9A84C]"/>
                <span className="text-[#EDE8DC]/60 text-xs truncate">{m.email}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
